import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchAvailableServices } from "@/lib/services/appointmentModalService";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";

/**
 * Journal-bookable services per master, for the drag & drop guardrail.
 *
 * Shares the `["availableServices", masterId]` key with `useAvailableServices`, so the
 * appointment modal and a drag pull on the same cache entry and the same request. The
 * answers land in `ServiceRegistry` (the fetcher writes them), which is what makes
 * `getLoaded` a synchronous read — a drag has to judge a column on every pointer move
 * and cannot wait on a promise to do it.
 */

const STALE_TIME = 5 * 60 * 1000;

export const masterServicesKey = (masterId: number) => ["availableServices", masterId];

export type MasterServicesAccess = {
  /** Warms the cache for every master a drag could land on. Fire-and-forget. */
  prefetchMany: (masterIds: number[]) => void;
  /** Awaits the list for one master; resolves to undefined when the request fails. */
  ensureLoaded: (masterId: number) => Promise<number[] | undefined>;
  /** Already-known service ids, or undefined when the list has never loaded. */
  getLoaded: (masterId: number) => number[] | undefined;
};

export function useMasterServices(): MasterServicesAccess {
  const queryClient = useQueryClient();

  return useMemo<MasterServicesAccess>(
    () => ({
      prefetchMany(masterIds) {
        for (const id of new Set(masterIds.map(Number))) {
          if (!Number.isFinite(id) || id <= 0) continue;
          void queryClient.prefetchQuery({
            queryKey: masterServicesKey(id),
            queryFn: () => fetchAvailableServices(id),
            staleTime: STALE_TIME,
          });
        }
      },

      async ensureLoaded(masterId) {
        const id = Number(masterId);
        if (!Number.isFinite(id) || id <= 0) return undefined;
        try {
          await queryClient.fetchQuery({
            queryKey: masterServicesKey(id),
            queryFn: () => fetchAvailableServices(id),
            staleTime: STALE_TIME,
          });
        } catch {
          // A failed lookup is reported as "unknown", never as "offers nothing".
          return undefined;
        }
        return getServiceRegistry().getJournalServiceIds(id);
      },

      getLoaded(masterId) {
        return getServiceRegistry().getJournalServiceIds(Number(masterId));
      },
    }),
    [queryClient]
  );
}
